import { useCallback } from 'react';
import type { SprintResult, TierCheckpoint, UserProfile } from '../types';
import {
  checkpointLabel,
  firstTrinIdInTier,
  tierTitle,
  trinLabel,
} from '../lib/curriculum';
import { getTrin } from '../data/curriculum';
import { getCheckpointRecord } from '../lib/stats';
import { useEnterAction } from '../lib/useEnterAction';
import LearningBenchmarkBar from './LearningBenchmarkBar';

interface SprintSummaryProps {
  result: SprintResult;
  checkpoint: TierCheckpoint;
  user: UserProfile;
  onRepeat: () => void;
  onContinue: () => void;
  onBack: () => void;
}

export default function SprintSummary({
  result,
  checkpoint,
  user,
  onRepeat,
  onContinue,
  onBack,
}: SprintSummaryProps) {
  const record = getCheckpointRecord(user, result.checkpointId);
  const nextTrinId = firstTrinIdInTier(checkpoint.beforeTierId);
  const nextTrin = nextTrinId ? getTrin(nextTrinId) : null;
  const nextTierLabel = tierTitle(checkpoint.beforeTierId);

  const onEnterPrimary = useCallback(() => {
    onRepeat();
  }, [onRepeat]);

  useEnterAction(onEnterPrimary);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 animate-fade-in-up">
      <header className="text-center">
        <p className="label text-amber-700 dark:text-amber-300">Test afsluttet</p>
        <h1 className="mt-1 text-3xl font-semibold text-strong">
          {checkpointLabel(checkpoint)}
        </h1>
      </header>

      <LearningBenchmarkBar
        checkpointId={result.checkpointId}
        words={result.wordsCompleted}
        variant="full"
      />

      {result.isNewRecord && (
        <div className="rounded-2xl bg-amber-100 p-4 text-center ring-1 ring-amber-200 dark:bg-amber-500/10 dark:ring-amber-500/30">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            Ny personlig rekord!
          </p>
          <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-200/80">
            Du skrev flere korrekte ord end sidst.
          </p>
        </div>
      )}

      <div className="rounded-2xl bg-amber-100 p-4 text-center ring-1 ring-amber-200 dark:bg-amber-500/10 dark:ring-amber-500/30">
        <p className="text-sm text-amber-900 dark:text-amber-200">
          {result.wordsCompleted} korrekte ord på 1 minut
          {result.wordsSkipped > 0
            ? ` · ${result.wordsSkipped} sprunget over`
            : ''}
        </p>
        {record && record.attempts > 1 && !result.isNewRecord && (
          <p className="mt-2 text-xs opacity-80">
            Din rekord er {record.bestWords} ord
          </p>
        )}
      </div>

      <section className="grid grid-cols-2 gap-3">
        <BigStat label="Korrekte ord" value={String(result.wordsCompleted)} />
        <BigStat label="Sprunget over" value={String(result.wordsSkipped)} />
      </section>

      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={onRepeat}
          className="flex-1 rounded-xl bg-indigo-500 px-4 py-3 font-semibold text-white shadow-sm transition hover:bg-indigo-400 dark:shadow-lg dark:shadow-indigo-500/30"
        >
          Prøv igen
        </button>
        <button
          type="button"
          onClick={onContinue}
          disabled={!nextTrin}
          className={
            !nextTrin
              ? 'flex-1 cursor-not-allowed rounded-xl bg-zinc-100 px-4 py-3 font-semibold text-zinc-400 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:text-zinc-600 dark:ring-white/5'
              : 'flex-1 btn-secondary py-3 font-semibold'
          }
        >
          {`Fortsæt til ${nextTierLabel}`}
        </button>
        <button type="button" onClick={onBack} className="flex-1 btn-secondary py-3 font-semibold">
          Tilbage til oversigt
        </button>
      </div>
      {nextTrin && (
        <p className="text-center text-xs text-faint">
          Næste: {trinLabel(nextTrin)} — {nextTrin.title}
        </p>
      )}
      <p className="text-center text-xs text-faint">Enter: prøv igen</p>
    </div>
  );
}

function BigStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface p-4">
      <p className="label">{label}</p>
      <p className="mt-1 font-mono text-3xl text-strong">{value}</p>
    </div>
  );
}
